import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import GoogleSignInButton from '../components/GoogleSignInButton';

function buildHostedUIUrl(provider: 'Google' = 'Google'): string {
  const base = (config.cognitoOAuthDomain || '').replace(/\/$/, '');
  const redirect = encodeURIComponent(config.cognitoOAuthRedirectSignIn || `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`);
  const scope = encodeURIComponent('openid email profile');
  return `${base}/oauth2/authorize?identity_provider=${provider}&redirect_uri=${redirect}&response_type=CODE&client_id=${config.cognitoClientId}&scope=${scope}`;
}

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userAlreadyExists, setUserAlreadyExists] = useState(false);
  const { signUp } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUserAlreadyExists(false);
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await signUp(email.trim(), password);
      setSuccess(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign up failed';
      const isAlreadyExists =
        /already exists|UsernameExistsException|user already exists/i.test(message) ||
        message.includes('User already exists');
      setUserAlreadyExists(!!isAlreadyExists);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    const confirmUrl = `/confirm?email=${encodeURIComponent(email.trim())}`;
    return (
      <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem' }}>
        <div style={{ padding: '1.5rem', background: '#f0fdf4', borderRadius: 12, color: '#166534', marginBottom: '1.5rem', border: '1px solid #bbf7d0' }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: '1.25rem' }}>Account created</p>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.95rem', lineHeight: 1.5 }}>
            Your account has been created. Check your email for a verification code, then confirm below to validate your account.
          </p>
        </div>
        <Link
          to={confirmUrl}
          style={{
            display: 'inline-block',
            padding: '0.75rem 1.5rem',
            background: '#0d9488',
            color: '#fff',
            borderRadius: 8,
            fontWeight: 600,
            textDecoration: 'none',
            marginRight: '0.75rem',
          }}
        >
          Enter verification code
        </Link>
        <Link to="/login" style={{ fontSize: '0.9rem', color: '#0d9488', fontWeight: 500 }}>
          Already verified? Sign in
        </Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 420, margin: '4rem auto', padding: '2rem' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '1rem', color: '#111' }}>
        Sign up
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
        Register with email and password, or use Google. Password: 8+ chars, upper, lower, number.
      </p>
      {config.cognitoOAuthDomain && config.cognitoClientId && (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <GoogleSignInButton href={buildHostedUIUrl('Google')} label="Sign up with Google" style={{ width: '100%', justifyContent: 'center' }} />
          </div>
          <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.85rem', textAlign: 'center' }}>— or —</p>
        </>
      )}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          Email (used as username)
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          Display name (optional — you can set or change this in Dashboard → Profile later)
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            placeholder="e.g. John"
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
            minLength={8}
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontWeight: 500, color: '#374151' }}>
          Confirm password
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            autoComplete="new-password"
            style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: '1rem',
            }}
          />
        </label>
        {error && (
          <div style={{ padding: '0.75rem', background: '#fef2f2', color: '#b91c1c', borderRadius: 8, fontSize: '0.9rem' }}>
            {error}
            {userAlreadyExists && (
              <p style={{ margin: '0.75rem 0 0', color: '#374151' }}>
                Didn’t receive the verification code? Go to the confirm page, enter your email, and click <strong>Resend code</strong> to get a new one.
              </p>
            )}
          </div>
        )}
        {userAlreadyExists && (
          <Link
            to={`/confirm?email=${encodeURIComponent(email.trim())}`}
            style={{
              display: 'inline-block',
              padding: '0.75rem 1.25rem',
              background: '#f0fdf4',
              color: '#166534',
              border: '1px solid #86efac',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: '0.95rem',
              textDecoration: 'none',
            }}
          >
            Go to Confirm email → Resend code
          </Link>
        )}
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: '0.75rem 1.25rem',
            background: loading ? '#9ca3af' : '#0d9488',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '1rem',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Creating account…' : 'Sign up'}
        </button>
      </form>
      <p style={{ marginTop: '1.25rem', color: '#6b7280', fontSize: '0.9rem' }}>
        Already have an account? <Link to="/login" style={{ color: '#0d9488', fontWeight: 600 }}>Sign in</Link>
        {' · '}
        Already signed up but need to verify? <Link to="/confirm" style={{ color: '#0d9488', fontWeight: 600 }}>Enter verification code</Link>
      </p>
      <Link to="/" style={{ display: 'inline-block', marginTop: '1rem', color: '#6b7280', fontSize: '0.9rem' }}>
        ← Back to home
      </Link>
    </div>
  );
}
